export enum PayPalAcp3dsAuthenticationReason {
  Successful = 'SUCCESSFUL',
  Attempted = 'ATTEMPTED',
  Failure = 'FAILURE',
  Error = 'ERROR',
  Skipped = 'SKIPPED_BY_BUYER',
  Bypassed = 'BYPASSED',
  Unavailable = 'UNAVAILABLE',
  Ineligible = 'CARD_INELIGIBLE',
}

export enum PayPalAcp3dsAuthenticationResult {
  Y = 'Y',
  N = 'N',
  R = 'R',
  A = 'A',
  U = 'U',
  C = 'C',
}

export enum PayPalAcp3dsAuthenticationStatus {
  Yes = 'YES',
  No = 'NO',
  Error = 'ERROR',
}

export enum PayPalAcp3dsEnrollmentStatus {
  Y = 'Y',
  N = 'N',
  U = 'U',
  B = 'B',
}

export enum PayPalAcp3dsLiabilityShift {
  Possible = 'Possible',
  No = 'No',
  Unknown = 'Unknown',
}

export enum PayPalAcp3dsAction {
  Continue = 1,
  Retry,
  NewCard,
}

export enum PayPalAcp3dsContingency {
  WhenRequired = 'SCA_WHEN_REQUIRED',
  Always = 'SCA_ALWAYS',
}

type PayPalAcp3dsLegacyResponse = {
  authenticationReason?: PayPalAcp3dsAuthenticationReason;
  authenticationStatus?: PayPalAcp3dsAuthenticationStatus;
  liabilityShifted?: boolean;
  liabilityShift?: PayPalAcp3dsLiabilityShift;
  enrollmentStatus?: never;
  authenticationResult?: never;
};

type PayPalAcp3dsCurrentResponse = {
  authenticationReason?: never;
  authenticationStatus?: never;
  liabilityShifted?: never;
  liabilityShift?: PayPalAcp3dsLiabilityShift;
  enrollmentStatus?: PayPalAcp3dsEnrollmentStatus;
  authenticationResult?: PayPalAcp3dsAuthenticationResult;
};

export type PayPalAcpHostedFieldsSubmit3dsResponse =
  | PayPalAcp3dsLegacyResponse
  | PayPalAcp3dsCurrentResponse;

type PayPalAcp3DsResponseActionPair = {
  response: PayPalAcpHostedFieldsSubmit3dsResponse;
  action: PayPalAcp3dsAction;
};

// source: https://developer.paypal.com/docs/business/checkout/add-capabilities/3d-secure/#authenticationresult
// source: https://developer.paypal.com/docs/business/checkout/add-capabilities/3d-secure/#deprecated-3d-secure-parameters
const paypal3dsResponseActionPairs: PayPalAcp3DsResponseActionPair[] = [
  {
    response: {
      authenticationResult: PayPalAcp3dsAuthenticationResult.N,
      enrollmentStatus: PayPalAcp3dsEnrollmentStatus.Y,
      liabilityShift: PayPalAcp3dsLiabilityShift.No,
    },
    action: PayPalAcp3dsAction.NewCard,
  },
  {
    response: {
      authenticationResult: PayPalAcp3dsAuthenticationResult.R,
      enrollmentStatus: PayPalAcp3dsEnrollmentStatus.Y,
      liabilityShift: PayPalAcp3dsLiabilityShift.No,
    },
    action: PayPalAcp3dsAction.NewCard,
  },
  {
    response: {
      authenticationResult: PayPalAcp3dsAuthenticationResult.U,
      enrollmentStatus: PayPalAcp3dsEnrollmentStatus.Y,
      liabilityShift: PayPalAcp3dsLiabilityShift.Unknown,
    },
    action: PayPalAcp3dsAction.Retry,
  },
  {
    response: {
      authenticationResult: PayPalAcp3dsAuthenticationResult.U,
      enrollmentStatus: PayPalAcp3dsEnrollmentStatus.Y,
      liabilityShift: PayPalAcp3dsLiabilityShift.No,
    },
    action: PayPalAcp3dsAction.Retry,
  },
  {
    response: {
      authenticationResult: PayPalAcp3dsAuthenticationResult.C,
      enrollmentStatus: PayPalAcp3dsEnrollmentStatus.Y,
      liabilityShift: PayPalAcp3dsLiabilityShift.Unknown,
    },
    action: PayPalAcp3dsAction.Retry,
  },
  {
    response: {
      authenticationResult: undefined,
      enrollmentStatus: PayPalAcp3dsEnrollmentStatus.Y,
      liabilityShift: PayPalAcp3dsLiabilityShift.No,
    },
    action: PayPalAcp3dsAction.Retry,
  },
  {
    response: {
      authenticationResult: undefined,
      enrollmentStatus: PayPalAcp3dsEnrollmentStatus.U,
      liabilityShift: PayPalAcp3dsLiabilityShift.Unknown,
    },
    action: PayPalAcp3dsAction.Retry,
  },
  {
    response: {
      authenticationResult: undefined,
      enrollmentStatus: undefined,
      liabilityShift: PayPalAcp3dsLiabilityShift.Unknown,
    },
    action: PayPalAcp3dsAction.Retry,
  },
  {
    response: {
      authenticationReason: PayPalAcp3dsAuthenticationReason.Failure,
      authenticationStatus: PayPalAcp3dsAuthenticationStatus.Error,
      liabilityShifted: false,
    },
    action: PayPalAcp3dsAction.Retry,
  },
  {
    response: {
      authenticationReason: PayPalAcp3dsAuthenticationReason.Failure,
      authenticationStatus: PayPalAcp3dsAuthenticationStatus.Error,
      liabilityShifted: undefined,
    },
    action: PayPalAcp3dsAction.Retry,
  },
  {
    response: {
      authenticationReason: PayPalAcp3dsAuthenticationReason.Error,
      authenticationStatus: PayPalAcp3dsAuthenticationStatus.Error,
      liabilityShifted: false,
    },
    action: PayPalAcp3dsAction.Retry,
  },
  {
    response: {
      authenticationReason: PayPalAcp3dsAuthenticationReason.Error,
      authenticationStatus: PayPalAcp3dsAuthenticationStatus.Error,
      liabilityShifted: undefined,
    },
    action: PayPalAcp3dsAction.Retry,
  },
  {
    response: {
      authenticationReason: PayPalAcp3dsAuthenticationReason.Skipped,
      authenticationStatus: PayPalAcp3dsAuthenticationStatus.No,
      liabilityShifted: false,
    },
    action: PayPalAcp3dsAction.Retry,
  },
  {
    response: {
      authenticationReason: PayPalAcp3dsAuthenticationReason.Unavailable,
      authenticationStatus: PayPalAcp3dsAuthenticationStatus.Error,
      liabilityShifted: undefined,
    },
    action: PayPalAcp3dsAction.Retry,
  },
  {
    response: {
      authenticationReason: PayPalAcp3dsAuthenticationReason.Failure,
      authenticationStatus: PayPalAcp3dsAuthenticationStatus.No,
      liabilityShifted: false,
    },
    action: PayPalAcp3dsAction.NewCard,
  },
];

export const find3dsAction = (
  paypal3dsResponse: PayPalAcpHostedFieldsSubmit3dsResponse
): PayPalAcp3dsAction => {
  const result = paypal3dsResponseActionPairs.find(pair => {
    const { response } = pair;
    if (
      response.authenticationResult ||
      response.enrollmentStatus ||
      response.liabilityShift
    ) {
      return (
        response.authenticationResult ===
          paypal3dsResponse.authenticationResult &&
        response.enrollmentStatus === paypal3dsResponse.enrollmentStatus &&
        response.liabilityShift === paypal3dsResponse.liabilityShift
      );
    }
    return (
      response.authenticationReason ===
        paypal3dsResponse.authenticationReason &&
      response.authenticationStatus ===
        paypal3dsResponse.authenticationStatus &&
      response.liabilityShifted === paypal3dsResponse.liabilityShifted
    );
  });
  return result?.action ?? PayPalAcp3dsAction.Continue;
};
